import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import '../../styled/map.css';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { Autocomplete, Box, Divider, Grid, Menu, MenuItem, Paper, Tooltip, Typography } from '@mui/material';
import { ArrowDropDown, Info } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DocumentDto, ShvkDocumentType } from '../../types/document';
import { ButtonEvent, TextFieldEvent } from '../../types/events';
import { OrganizationDto } from '../../types/organization';
import { TranslationKeys } from '../../types/translation';
import ShvkTextField from '../../styled/ShvkTextField';
import ShvkButton from '../../styled/ShvkButton';
import moment from 'moment';
import useStore from '../../store/storeContext';
import { convertEpochToIsoDateString } from '../../utils/dateUtils';

const PREFIX = 'PublishedDocuments';

const classes = {
    label: `${PREFIX}-table`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    [`& .${classes.label}`]: {
        borderRadius: theme.spacing(0.5),
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.75),
        paddingRight: theme.spacing(1.5),
        paddingLeft: theme.spacing(1.5),
        background: theme.palette.info.main,
        textTransform: 'uppercase',
    },
}));

type PublishedDocumentsFormField = 'selectedDocType' | 'selectedLargeReportPeriod' | 'selectedYearlyReportYear';

type Center = {
    lat: number;
    lng: number;
};

const containerStyle = {
    width: '100%',
    height: '70vh',
};

function PublishedDocuments() {
    const context = useStore();

    const [organizationMenu, setOrganizationMenu] = useState<HTMLElement | null>(null);

    const [center, setCenter] = useState<Center>({ lat: 65.0103, lng: 25.473505 });

    const [mapReady, setMapReady] = useState(false);

    const mapRef = useRef<google.maps.Map | null>(null);
    const clustererRef = useRef<MarkerClusterer | null>(null);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: 'AIzaSyCb8JWHi1F3KKOrez0Fk3AF1krVpE57jA0',
    });

    const [zoom, setZoom] = useState<number>(5);

    const localStore = useLocalObservable(() => ({
        selectedDocType: '',
        selectedLargeReportPeriod: '',
        selectedYearlyReportYear: '',
        selectedOrganization: {} as OrganizationDto,
        evaStart: '',
        evaEnd: '',

        get filteredAllPublishedDocuments(): DocumentDto[] {
            let documents = context.document.allPublishedDocuments;
            if (this.selectedDocType) {
                if (this.selectedDocType === 'YEARLY_REPORT') {
                    documents = documents.filter(
                        (document: DocumentDto) =>
                            document.documentType.code === this.selectedDocType ||
                            document.documentType.code === 'REGIONAL_YEARLY_REPORT',
                    );
                } else if (this.selectedDocType === 'LARGE_REPORT') {
                    documents = documents.filter(
                        (document: DocumentDto) =>
                            document.documentType.code === this.selectedDocType ||
                            document.documentType.code === 'REGIONAL_LARGE_REPORT',
                    );
                } else {
                    documents = documents.filter(
                        (document: DocumentDto) => document.documentType.code === this.selectedDocType,
                    );
                }
            }
            if (this.selectedYearlyReportYear) {
                documents = documents.filter(
                    (document: DocumentDto) =>
                        document.yearBegin?.toString() === this.selectedYearlyReportYear.toString(),
                );
            }
            if (this.selectedLargeReportPeriod) {
                documents = documents.filter(
                    (document: DocumentDto) =>
                        document.yearBegin?.toString() + ' - ' + document.yearEnd?.toString() ===
                        this.selectedLargeReportPeriod,
                );
            }
            if (context.document.isEvaDocument(this.selectedDocType)) {
                documents = documents.filter(
                    (document: DocumentDto) =>
                        convertEpochToIsoDateString(document.acceptedDate!) >= this.evaStart &&
                        convertEpochToIsoDateString(document.acceptedDate!) <= this.evaEnd,
                );
            }
            return documents;
        },

        get organizationLocationsWithPublishedDocuments(): OrganizationDto[] {
            return context.organization.organizationLocations.filter((organization: OrganizationDto) =>
                this.filteredAllPublishedDocuments.some(
                    (document: DocumentDto) => document.organization.id === organization.id,
                ),
            );
        },

        get filteredOrganizationPublishedDocuments(): DocumentDto[] {
            return this.filteredAllPublishedDocuments
                .filter((document: DocumentDto) => document.organization.id === this.selectedOrganization.id)
                .sort((doc1, doc2) => (doc1.acceptedDate! < doc2.acceptedDate! ? 1 : -1));
        },

        changeDate(event: TextFieldEvent): void {
            if (event.target.id === 'eva-filter-start-date') {
                this.evaStart = event.target.value;
            } else {
                this.evaEnd = event.target.value;
            }
        },

        handleChange(event: TextFieldEvent, field: PublishedDocumentsFormField): void {
            this[field] = event.target.value;
            if (field === 'selectedDocType') {
                this.selectedLargeReportPeriod = '';
                this.selectedYearlyReportYear = '';
            }
        },

        handleSelectOrganization(organization: OrganizationDto): void {
            this.selectedOrganization = organization;
            handleGetOrganizationClose();
            context.dialog.openPublishedOrganizationDocumentsDialog(
                organization,
                this.filteredOrganizationPublishedDocuments,
            );
        },
    }));

    useEffect(() => {
        setCenter({ lat: 65.0103, lng: 25.473505 });
        setZoom(5);
        runInAction(() => {
            localStore.evaStart = moment([2020, 0, 1]).format('YYYY[-]MM[-]DD');
            localStore.evaEnd = moment().format('YYYY[-]MM[-]DD');
        });
        return () => {
            clustererRef.current?.clearMarkers();
            mapRef.current = null;
            clustererRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!mapRef.current || !clustererRef.current) return;

        const organizations = localStore.organizationLocationsWithPublishedDocuments;

        clustererRef.current.clearMarkers();

        const locale = context.localization.locale;

        const markers = organizations.map((org) => {
            const organizationName = org.name[locale] || org.name.fi_FI || org.name.sv_SE || org.name.en_GB || '';
            const marker = new google.maps.Marker({
                position: { lat: org.latitude, lng: org.longitude },
                title: organizationName,
                map: mapRef.current,
                label: {
                    text: organizationName,
                    color: '#FFF',
                    className: `${classes.label}`,
                },
            });
            marker.addListener('click', (): void => {
                localStore.handleSelectOrganization(org);
            });

            return marker;
        });
        clustererRef.current.addMarkers(markers);
    }, [mapReady, localStore.organizationLocationsWithPublishedDocuments]);

    function changeCenter(lat: number, lng: number): void {
        setCenter({ lat, lng });
    }

    function changeZoom(zoom: number): void {
        setZoom(zoom);
    }

    function findSelectedOrganization(organization: OrganizationDto | null): void {
        if (!organization) {
            changeCenter(65.0103, 25.473505);
            changeZoom(5);
        } else {
            changeCenter(organization.latitude, organization.longitude);
            changeZoom(9);
        }
    }

    const handleGetOrganizationClick = (event: ButtonEvent): void => {
        setOrganizationMenu(event.currentTarget);
    };

    const handleGetOrganizationClose = (): void => {
        setOrganizationMenu(null);
    };

    const documentTypes = context.document.documentTypes.filter(
        (type) => type.code !== 'REGIONAL_YEARLY_REPORT' && type.code !== 'REGIONAL_LARGE_REPORT',
    );

    const { translate, locale } = context.localization;

    return (
        <StyledPaper>
            <Box px={2} py={1} display={'flex'} alignItems={'center'}>
                <Typography variant="h6" component="h4" style={{ display: 'inline-block' }}>
                    {translate('PUBLISHED_DOCUMENTS')}
                </Typography>
                <Tooltip title={translate('etusivu_dokumenttien_haku_2')}>
                    <Info color="primary" sx={{ ml: 1 }} />
                </Tooltip>
            </Box>
            <Divider />
            <Box p={2}>
                <ShvkButton sx={{ mb: 6 }} onClick={handleGetOrganizationClick} endIcon={<ArrowDropDown />} fullWidth>
                    {translate('ORGANIZATION_BASED_SEARCH')}
                </ShvkButton>
                <Menu
                    id="get-documents-by-organization"
                    anchorEl={organizationMenu}
                    keepMounted
                    open={Boolean(organizationMenu)}
                    onClose={handleGetOrganizationClose}
                >
                    {localStore.organizationLocationsWithPublishedDocuments.map((organization: OrganizationDto) => (
                        <MenuItem
                            key={organization.id}
                            onClick={() => localStore.handleSelectOrganization(organization)}
                        >
                            {organization.name[locale] ||
                                organization.name.fi_FI ||
                                organization.name.sv_SE ||
                                organization.name.en_GB}
                        </MenuItem>
                    ))}
                </Menu>
                <Autocomplete
                    id="locate-organization"
                    options={localStore.organizationLocationsWithPublishedDocuments}
                    getOptionLabel={(option: OrganizationDto): string =>
                        option.name[locale] || option.name.fi_FI || option.name.sv_SE || option.name.en_GB
                    }
                    sx={{ mb: 2 }}
                    fullWidth
                    size="small"
                    openOnFocus
                    selectOnFocus
                    onChange={(_event, newValue) => findSelectedOrganization(newValue)}
                    renderInput={(params): JSX.Element => (
                        <ShvkTextField {...params} label={translate('LOCATE_COUNCIL_ON_MAP_TIP')} variant="outlined" />
                    )}
                />
                <ShvkTextField
                    select
                    id="filter-docs-by-type"
                    onChange={(event): void => localStore.handleChange(event, 'selectedDocType')}
                    label={translate('FILTER_DOCUMENTS')}
                    value={localStore.selectedDocType}
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 2 }}
                    size="small"
                >
                    <MenuItem value={''}>{translate('ALL')}</MenuItem>
                    {documentTypes.map((docType: ShvkDocumentType) => (
                        <MenuItem key={docType.id} value={docType.code}>
                            {translate(docType.code as keyof TranslationKeys)}
                        </MenuItem>
                    ))}
                </ShvkTextField>
                {localStore.selectedDocType !== 'LARGE_REPORT' ? null : (
                    <ShvkTextField
                        select
                        id="large-report-period"
                        onChange={(event): void => localStore.handleChange(event, 'selectedLargeReportPeriod')}
                        label={translate('SELECT_DOCUMENT_PERIOD')}
                        value={localStore.selectedLargeReportPeriod}
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 2 }}
                        size="small"
                    >
                        {context.document.largeReportTermsFromPublishedDocuments.map((term: string) => (
                            <MenuItem key={term} value={term}>
                                {term}
                            </MenuItem>
                        ))}
                    </ShvkTextField>
                )}
                {localStore.selectedDocType !== 'YEARLY_REPORT' ? null : (
                    <ShvkTextField
                        select
                        id="yearly-report-select-year"
                        onChange={(event): void => localStore.handleChange(event, 'selectedYearlyReportYear')}
                        label={translate('SELECT_PERIOD')}
                        value={localStore.selectedYearlyReportYear.toString()}
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 2 }}
                        size="small"
                    >
                        {context.document.yearlyReportYearsFromPublishedDocuments.map((year: number) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </ShvkTextField>
                )}
                {!context.document.isEvaDocument(localStore.selectedDocType) ? null : (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <Typography>{translate('ACCEPTED_DATE_RANGE_TIP')}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <ShvkTextField
                                fullWidth
                                id="eva-filter-start-date"
                                label={translate('START_DATE')}
                                type="date"
                                variant="outlined"
                                size="small"
                                onChange={(event: TextFieldEvent) => localStore.changeDate(event)}
                                value={localStore.evaStart}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <ShvkTextField
                                fullWidth
                                id="eva-filter-end-date"
                                label={translate('END_DATE')}
                                type="date"
                                variant="outlined"
                                size="small"
                                onChange={(event: TextFieldEvent) => localStore.changeDate(event)}
                                value={localStore.evaEnd}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
                {isLoaded && (
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={zoom}
                        onLoad={(map): void => {
                            mapRef.current = map;
                            clustererRef.current = new MarkerClusterer({
                                map,
                                markers: [],
                            });
                            setMapReady(true);
                        }}
                    ></GoogleMap>
                )}
            </Box>
        </StyledPaper>
    );
}

export default observer(PublishedDocuments);
